import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [showDrawer, setShowDrawer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', email: '', phone: '', role: 'user', password: '', password_confirmation: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('admin');

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setUsers(res.data || []);
    } catch (err) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const confirmDelete = (user) => {
    setItemToDelete(user);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/users/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setUsers(users.filter(u => u.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('User deleted successfully!');
    } catch (err) {
      toast.error('Error deleting user.');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (user) => {
    setFormData({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'user',
      password: '',
      password_confirmation: ''
    });
    setIsEditing(true);
    setShowDrawer(true);
  };

  const handleOpenCreate = () => {
    setFormData({ id: null, name: '', email: '', phone: '', role: 'user', password: '', password_confirmation: '' });
    setIsEditing(false);
    setShowDrawer(true);
  };

  const executeSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = isEditing ? `${API_URL}/api/admin/users/${formData.id}` : `${API_URL}/api/admin/users`;
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const res = await response.json();
      if (!response.ok) throw new Error(res.message || 'Failed to save user');
      
      if (isEditing) {
        setUsers(users.map(u => u.id === formData.id ? { ...u, ...formData } : u));
        toast.success('User updated successfully!');
      } else {
        setUsers([res.data, ...users]);
        toast.success('User created! OTP verification email sent.');
      }
      setShowDrawer(false);
    } catch (err) {
      toast.error(err.message || 'Error saving user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        .admin-page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .admin-page-title { font-size: 24px; font-weight: 700; color: #0f172a; margin: 0; }

        .table-container { background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden; }
        .admin-table { width: 100%; border-collapse: collapse; text-align: left; }
        .admin-table th { background: #f8fafc; padding: 16px; font-size: 13px; font-weight: 600; color: #64748b; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.5px; }
        .admin-table td { padding: 16px; font-size: 14px; color: #334155; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
        .admin-table tr:last-child td { border-bottom: none; }
        .admin-table tr:hover td { background: #f8fafc; }

        .role-badge { padding: 4px 10px; border-radius: 50px; font-size: 12px; font-weight: 600; text-transform: capitalize; }
        .role-admin { background: #d1fae5; color: #065f46; }
        .role-user { background: #e0e7ff; color: #3730a3; }

        .action-btns { display: flex; gap: 8px; }
        .btn-action { width: 32px; height: 32px; border-radius: 6px; border: 1px solid #e2e8f0; background: white; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .btn-action:hover { border-color: #cbd5e1; color: #0f172a; background: #f1f5f9; }
        .btn-action.edit:hover { border-color: #93c5fd; color: #2563eb; background: #eff6ff; }
        .btn-action.delete:hover { border-color: #fca5a5; color: #ef4444; background: #fef2f2; }

        .user-avatar { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, #CFA047, #061830); color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; }
        .user-info { display: flex; align-items: center; gap: 12px; }
        .user-name { font-weight: 600; color: #0f172a; }
        .user-email { font-size: 12px; color: #64748b; margin-top: 2px; }

        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: flex-start; z-index: 999; padding: 40px 20px; overflow-y: auto; }
        @keyframes slideDown { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        /* Slide-in Drawer */
        .pk-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes pk-drop { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .pk-drawer { width: 480px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.18); animation: pk-drop 0.28s cubic-bezier(0.22,1,0.36,1); }
        .pk-drawer-hd { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .pk-drawer-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .pk-drawer-close { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .pk-drawer-close:hover { background: rgba(255,255,255,0.22); }
        .pk-form-panel { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }
        .pk-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: flex-end; gap: 12px; background: #f9fbfc; flex-shrink: 0; }
        
        .pk-float-group { position: relative; margin-bottom: 6px; }
        .pk-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .pk-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .pk-float-inp:focus + .pk-float-label, .pk-float-inp:not(:placeholder-shown) + .pk-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .pk-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        .pk-float-select { padding-top: 20px; padding-bottom: 6px; appearance: none; background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: right 16px center; background-size: 16px; padding-right: 40px; cursor: pointer; }
        .pk-float-select + .pk-float-label { top: 7px; font-size: 10.5px; color: #9babbf; font-weight: 700; }
        .pk-float-select:focus + .pk-float-label { color: #CFA047; }
        
        .pk-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .pk-btn-cancel:hover { background: #f7f9fc; }
        .pk-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .pk-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
        .pk-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .tabs { display: flex; gap: 24px; border-bottom: 1px solid #e2e8f0; margin-bottom: 24px; }
        .tab-btn { background: none; border: none; padding: 0 0 12px 0; font-size: 15px; font-weight: 600; color: #64748b; cursor: pointer; position: relative; transition: color 0.2s; }
        .tab-btn:hover { color: #0f172a; }
        .tab-btn.active { color: #CFA047; }
        .tab-btn.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 2px; background: #CFA047; border-radius: 2px 2px 0 0; }
        .tab-badge { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 20px; font-size: 12px; margin-left: 8px; }
        .tab-btn.active .tab-badge { background: #fdf5e6; color: #CFA047; }
      `}</style>

      <div className="admin-page-header">
        <h2 className="admin-page-title">User Management</h2>
        <button onClick={handleOpenCreate} style={{ background: 'linear-gradient(135deg, #CFA047, #061830)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 600, fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(207, 160, 71, 0.3)' }}>
          <i className="fa-solid fa-plus" style={{ marginRight: '8px' }}></i> Create User
        </button>
      </div>

      <div className="tabs">
        <button className={`tab-btn ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')}>
          Administrators <span className="tab-badge">{users.filter(u => u.role === 'admin').length}</span>
        </button>
        <button className={`tab-btn ${activeTab === 'user' ? 'active' : ''}`} onClick={() => setActiveTab('user')}>
          Standard Users <span className="tab-badge">{users.filter(u => u.role !== 'admin').length}</span>
        </button>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Joined Date</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>Loading users...</td></tr>
            ) : users.filter(u => activeTab === 'admin' ? u.role === 'admin' : u.role !== 'admin').length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>No {activeTab === 'admin' ? 'administrators' : 'users'} found.</td></tr>
            ) : (
              users.filter(u => activeTab === 'admin' ? u.role === 'admin' : u.role !== 'admin').map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="user-name">{user.name}</div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.phone || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.role === 'admin' ? 'role-admin' : 'role-user'}`}>
                      {user.role || 'User'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-btns" style={{ justifyContent: 'center' }}>
                      {activeTab === 'admin' && (
                        <button className="btn-action edit" onClick={() => handleEditClick(user)} title="Edit">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                      )}
                      <button className="btn-action delete" onClick={() => confirmDelete(user)} title="Delete">
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {itemToDelete && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setItemToDelete(null); }} style={{ alignItems: 'center' }}>
          <div style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'slideDown 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete User</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete the user <strong>"{itemToDelete.name}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)} disabled={deleting}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', border: 'none', background: deleting ? '#fca5a5' : '#ef4444', color: 'white' }} onClick={executeDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {showDrawer && (
        <div className="pk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDrawer(false); }}>
          <div className="pk-drawer">
            <div className="pk-drawer-hd">
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CFA047', fontSize: '16px' }}>
                  <i className={`fa-solid ${isEditing ? 'fa-pen' : 'fa-user-plus'}`}></i>
                </div>
                <div>
                  <h3 className="pk-drawer-title">{isEditing ? 'Edit User' : 'Create New User'}</h3>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '2px' }}>
                    {isEditing ? 'Update user roles and info' : 'Add an admin or standard user'}
                  </div>
                </div>
              </div>
              <button className="pk-drawer-close" onClick={() => setShowDrawer(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="pk-form-panel">
              <div className="pk-float-group">
                <input className="pk-float-inp" type="text" placeholder=" " value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                <label className="pk-float-label">Full Name</label>
              </div>

              <div className="pk-float-group">
                <input className="pk-float-inp" type="email" placeholder=" " value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                <label className="pk-float-label">Email Address</label>
              </div>

              <div className="pk-float-group">
                <input className="pk-float-inp" type="text" placeholder=" " value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                <label className="pk-float-label">Phone Number</label>
              </div>

              <div className="pk-float-group">
                <select className="pk-float-inp pk-float-select" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}>
                  <option value="user">Standard User</option>
                  <option value="admin">Administrator</option>
                </select>
                <label className="pk-float-label">User Role</label>
              </div>

              <div className="pk-float-group" style={{ marginTop: '12px' }}>
                <input className="pk-float-inp" type={showPassword ? "text" : "password"} placeholder=" " value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                <label className="pk-float-label">{isEditing ? 'New Password (Leave blank to keep)' : 'Initial Password'}</label>
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '16px', top: '20px', background: 'none', border: 'none', color: '#9babbf', cursor: 'pointer' }}>
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              
              <div className="pk-float-group">
                <input className="pk-float-inp" type={showConfirmPassword ? "text" : "password"} placeholder=" " value={formData.password_confirmation} onChange={e => setFormData({...formData, password_confirmation: e.target.value})} />
                <label className="pk-float-label">Confirm Password</label>
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '16px', top: '20px', background: 'none', border: 'none', color: '#9babbf', cursor: 'pointer' }}>
                  <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="pk-df">
              <button className="pk-btn-cancel" onClick={() => setShowDrawer(false)} disabled={saving}>Cancel</button>
              <button className="pk-btn-save" onClick={executeSave} disabled={saving}>
                {saving ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create User')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUsers;

